import branding from '../../src/branding';
// import { createWebHistory } from '@ionic/vue-router';
import { RouteRecordRaw, createRouter, createWebHistory } from 'vue-router';

import { accountService } from '../../src/services/accounts';
import { employeeService } from '../../src/services/employees';
import { employeeGroupService } from '../../src/services/employee-groups';

import { showMenu } from '../../src/globals/refs';

import { Breadcrumb } from '../../src/globals/breadcrumbs';
import { accountStore } from '../../src/store/accounts';
import institutionService from '../../src/services/institutions';
import { session } from '../../src/globals/session';
import { installStaleChunkReload } from '../../src/globals/stale-chunk-reload';

import { codeBackend } from '../../src/globals/auth';

const routes: Array<RouteRecordRaw> = [
  {
    name: 'authentication:code',
    path: '/authentication/code/',
    component: () => import('../../src/views/authentication/LoginViaCode.vue'),
    meta: {
      allowGuests: true,
      hideChrome: true,
      transparentStatusBar: true,
    },
  },

  {
    name: 'authentication:login',
    path: '/authentication/login/',
    component: () => import('../../src/views/authentication/Login.vue'),
    meta: {
      allowGuests: true,
      hideChrome: true,
      transparentStatusBar: true,
    },
  },

  {
    name: 'authentication:set-new-password',
    path: '/authentication/reset-password/:token/',
    component: () => import('../../src/views/authentication/SetNewPassword.vue'),
    meta: {
      allowGuests: true,
      hideChrome: true,
      transparentStatusBar: true,
    },
  },

  {
    name: 'authentication:reset-password',
    path: '/authentication/reset-password/',
    component: () => import('../../src/views/authentication/ResetPassword.vue'),
    meta: {
      allowGuests: true,
      hideChrome: true,
      transparentStatusBar: true,
    },
  },

  {
    name: 'authentication:logout',
    path: '/authentication/logout/',
    component: () => import('../../src/views/authentication/Logout.vue'),
    meta: {
      allowGuests: true,
      hideChrome: true,
      transparentStatusBar: true,
    },
  },

  // Time Tracking
  {
    name: 'time-tracking',
    path: '/time-tracking/',
    component: () => import('../../src/views/TimeTracking.vue'),
    meta: {
      requiresEmployee: true,
    }
  },

  // Terminal Mode
  {
    path: '/terminal/admin/',
    component: () => import('../../src/views/terminal/TerminalAdmin.vue'),
    meta: {
      allowGuests: true,
      hideChrome: true,
    },
  },
  {
    path: '/terminal/',
    component: () => import('../../src/views/terminal/TerminalHome.vue'),
    meta: {
      allowGuests: true,
      hideChrome: true,
    },
  },
  {
    path: '/terminal/employees/:id/',
    component: () => import('../../src/views/terminal/AuthenticatedTracking.vue'),
    meta: {
      allowGuests: true,
      hideChrome: true,
    },
  },
  {
    path: '/terminal/employees/:id/hourly-balance/',
    component: () => import('../../src/views/terminal/HourlyBalance.vue'),
    meta: {
      allowGuests: true,
      hideChrome: true,
    },
  },
  {
    path: '/terminal/employees/:id/profile/',
    component: () => import('../../src/views/terminal/UserAccount.vue'),
    meta: {
      allowGuests: true,
      hideChrome: true,
    },
  },
  {
    path: '/terminal/not-assigned-to-institution/',
    component: () => import('../../src/views/terminal/NotAssignedToInstitution.vue'),
    meta: {
      allowGuests: true,
      hideChrome: true,
    },
  },
  {
    path: '/terminal/not-assigned-no-internet/',
    component: () => import('../../src/views/terminal/NotAssignedNoInternet.vue'),
    meta: {
      allowGuests: true,
      hideChrome: true,
    },
  },
  {
    path: '/terminal/token/:id/not-assigned/',
    component: () => import('../../src/views/terminal/TokenNotAssignedToEmployee.vue'),
    meta: {
      allowGuests: true,
      hideChrome: true,
    },
  },
  {
    path: '/terminal/guest/',
    component: () => import('../../src/views/terminal/Guest.vue'),
    meta: {
      allowGuests: true,
      hideChrome: true,
    },
  },

  // Tag categories
  {
    name: 'tag-categories:add',
    path: '/tags/categories/add/',
    component: () => import('../../src/views/TagCategoriesDetail.vue'),
    meta: { permissionSpace: 'project_categories' },
  },
  {
    name: 'tag-categories:detail',
    path: '/tags/categories/:id/',
    component: () => import('../../src/views/TagCategoriesDetail.vue'),
    meta: { permissionSpace: 'project_categories' },
  },
  {
    name: 'tag-categories:list',
    path: '/tags/categories/',
    component: () => import('../../src/views/TagCategories.vue'),
    meta: { permissionSpace: 'project_categories' },
  },

  // Tags
  {
    name: 'tags:add',
    path: '/tags/add/',
    component: () => import('../../src/views/TagsDetail.vue'),
    meta: {
      permissionSpace: 'projects',
      breadcrumbs: [
        new Breadcrumb('tags:list', async () => 'Labels'),
      ],
    },
  },
  {
    name: 'tags:detail',
    path: '/tags/:id/',
    redirect: (to) => ({ name: 'tags:detail:general', params: { id: to.params.id } }),
  },
  {
    name: 'tags:detail:general',
    path: '/tags/:id/general/',
    component: () => import('../../src/views/TagsDetail.vue'),
    meta: {
      permissionSpace: 'projects',
      breadcrumbs: [
        new Breadcrumb('tags:list', async () => 'Labels'),
      ],
    },
  },
  {
    name: 'tags:detail:restrictions',
    path: '/tags/:id/restrictions/',
    component: () => import('../../src/views/TagsDetail.vue'),
    meta: {
      permissionSpace: 'projects',
      breadcrumbs: [
        new Breadcrumb('tags:list', async () => 'Labels'),
      ],
    },
  },
  {
    name: 'tags:detail:daily-reports',
    path: '/tags/:id/daily-reports/',
    component: () => import('../../src/views/TagsDetail.vue'),
    meta: {
      permissionSpace: 'projects',
      breadcrumbs: [
        new Breadcrumb('tags:list', async () => 'Labels'),
      ],
    },
  },
  {
    name: 'tags:list',
    path: '/tags/',
    component: () => import('../../src/views/Tags.vue'),
    meta: { permissionSpace: 'projects' },
  },

  // Employee Groups
  {
    path: '/departments/:id/employees/',
    component: () => import('../../src/views/DepartmentsEmployees.vue'),
    meta: {
      permissionSpace: 'employee_groups',
      breadcrumbs: [
        new Breadcrumb('departments:list', async () => 'Abteilungen'),
        new Breadcrumb('departments:detail',
          async (route) => {
            const groupId = route.params.id;
            return (await employeeGroupService.retrieve(groupId)).data.name;
          },
          async (route) => {
            return { id: route.params.id }
          }
        ),
      ],
    },
  },
  {
    path: '/departments/add/',
    component: () => import('../../src/views/DepartmentsDetail.vue'),
    meta: {
      permissionSpace: 'employee_groups',
      breadcrumbs: [
        new Breadcrumb('departments:list', async () => 'Abteilungen'),
      ],
    },
  },
  {
    name: 'departments:detail',
    path: '/departments/:id/',
    component: () => import('../../src/views/DepartmentsDetail.vue'),
    meta: {
      permissionSpace: 'employee_groups',
      breadcrumbs: [
        new Breadcrumb('departments:list', async () => 'Abteilungen'),
      ],
    },
  },
  {
    name: 'departments:list',
    path: '/departments/',
    component: () => import('../../src/views/Departments.vue'),
    meta: { permissionSpace: 'employee_groups' },
  },

  // Managers
  {
    name: 'managers:add',
    path: '/managers/add/',
    component: () => import('../../src/views/ManagersDetail.vue'),
    meta: {
      permissionSpace: 'managers',
      breadcrumbs: [
        new Breadcrumb('managers:list', async () => 'Abteilungsleiter'),
      ],
    },
  },
  {
    name: 'managers:detail',
    path: '/managers/:id/',
    // component: () => import('../../src/views/Managers.vue'),
    component: () => import('../../src/views/ManagersDetail.vue'),
    meta: {
      permissionSpace: 'managers',
      breadcrumbs: [
        new Breadcrumb('managers:list', async () => 'Abteilungsleiter'),
      ],
    },
  },
  {
    name: 'managers:list',
    path: '/managers/',
    component: () => import('../../src/views/Managers.vue'),
    meta: { permissionSpace: 'managers' },
  },

  // Manager invites
  {
    name: 'manager-invites:list',
    path: '/manager-invites/',
    component: () => import('../../src/views/ManagerInvites.vue'),
    meta: { permissionSpace: 'manager-invites' },
  },

  // Devices
  {
    path: '/devices/add/',
    component: () => import('../../src/views/DevicesDetail.vue'),
    meta: {
      permissionSpace: 'devices',
      breadcrumbs: [
        new Breadcrumb('devices:list', async () => 'Hardware'),
      ],
    },
  },
  {
    name: 'devices:detail',
    path: '/devices/:id/',
    component: () => import('../../src/views/DevicesDetail.vue'),
    meta: {
      permissionSpace: 'devices',
      breadcrumbs: [
        new Breadcrumb('devices:list', async () => 'Hardware'),
      ],
    },
  },
  {
    name: 'devices:list',
    path: '/devices/',
    component: () => import('../../src/views/Devices.vue'),
    meta: { permissionSpace: 'devices' },
  },

  // Admin > Institutions
  {
    name: 'staff-institutions:absences',
    path: '/staff/institutions/:id/absences/',
    component: () => import('../../src/views/staff/institutions/details/Absences.vue'),
  },
  {
    name: 'staff-institutions:exports',
    path: '/staff/institutions/:id/exports/',
    component: () => import('../../src/views/staff/institutions/details/Exports.vue'),
  },
  {
    name: 'staff-institutions:custom-fields',
    path: '/staff/institutions/:id/custom-fields/',
    component: () => import('../../src/views/staff/institutions/details/CustomFields.vue'),
  },
  {
    name: 'staff-institutions:api-tokens',
    path: '/staff/institutions/:id/api-tokens/',
    component: () => import('../../src/views/staff/institutions/details/ApiTokens.vue'),
  },
  {
    name: 'staff-institutions:detail',
    path: '/staff/institutions/:id/',
    component: () => import('../../src/views/staff/institutions/details/CoreData.vue'),
  },
  {
    name: 'staff-institutions:list',
    path: '/staff/institutions/',
    component: () => import('../../src/views/staff/institutions/StaffInstitutions.vue'),
  },
  {
    name: 'staff-institutions:gen5-config',
    path: '/staff/institutions/:id/gen5-config/',
    component: () => import('../../src/views/staff/institutions/details/Gen5Config.vue'),
  },
  {
    name: 'staff-institutions:daily-reports',
    path: '/staff/institutions/:id/daily-reports/',
    component: () => import('../../src/views/staff/institutions/details/DailyReports.vue'),
  },
  {
    name: 'staff-institutions:invoices:detail',
    path: '/staff/institutions/:id/invoices/:invoiceId/',
    component: () => import('../../src/views/staff/institutions/details/InvoiceDetail.vue'),
  },
  {
    name: 'staff-institutions:invoices',
    path: '/staff/institutions/:id/invoices/',
    component: () => import('../../src/views/staff/institutions/details/Invoices.vue'),
  },
  {
    name: 'staff-institutions:permissions',
    path: '/staff/institutions/:id/permissions/',
    component: () => import('../../src/views/staff/institutions/details/Permissions.vue'),
  },

  // Timespans
  {
    path: '/timespans/:id/',
    component: () => import('../../src/views/TimespansDetail2.vue'),
    meta: {
      permissionSpace: 'timespans',
      breadcrumbs: [
        new Breadcrumb('time-tracking', async () => 'Stechuhr'),
      ],
    },
  },

  // Employees
  {
    name: 'employees:add',
    path: '/employees/add/',
    component: () => import('../../src/views/EmployeesDetail.vue'),
    meta: {
      permissionSpace: 'employees',
      breadcrumbs: [
        new Breadcrumb('employees:list', async () => 'Mitarbeiter'),
      ],
    },
  },
  {
    name: 'employees:settings',
    path: '/employees/:employeeId/settings/',
    component: () => import('../../src/views/Settings.vue'),
    meta: {
      permissionSpace: 'settings',
      breadcrumbs: [
        new Breadcrumb('employees:list', async () => 'Mitarbeiter'),
        new Breadcrumb('employees:detail',
          async (route) => {
            // The axios response interceptor returns undefined on Network Error (see src/axios.ts).
            const response = await employeeService.retrieve(route.params.employeeId as string);
            if (!response) return '';
            const e = response.data;
            return e.first_name + " " + e.last_name;
          },
          async (route) => {
            return {
              id: route.params.employeeId,
            }
          },
        ),
      ],
    },
  },
  {
    name: 'employees:detail',
    path: '/employees/:id/',
    component: () => import('../../src/views/EmployeesDetail.vue'),
    meta: {
      permissionSpace: 'employees',
      breadcrumbs: [
        new Breadcrumb('employees:list', async () => 'Mitarbeiter'),
      ],
    },
  },
  {
    name: 'employees:list',
    path: '/employees/',
    component: () => import('../../src/views/Employees.vue'),
    meta: { permissionSpace: 'employees' },
  },

  // Tokens
  { path: '/tokens/add/', redirect: '/devices/tokens/add/', },
  { path: '/tokens/', redirect: '/devices/tokens/', },
  {
    path: '/devices/tokens/add/',
    component: () => import('../../src/views/TokensAdd.vue'),
    meta: {
      permissionSpace: 'physical_tokens',
      breadcrumbs: [
        new Breadcrumb('tokens:list', async () => 'Token'),
      ],
    },
  },
  {
    name: 'tokens:list',
    path: '/devices/tokens/',
    component: () => import('../../src/views/Tokens.vue'),
    meta: { permissionSpace: 'physical_tokens' },
  },

  {
    name: 'devices:terminal-pin',
    path: '/devices/terminal-pin/',
    component: () => import('../../src/views/DevicesTerminalPin.vue'),
  },

  // Absence applications
  {
    name: 'absence-applications',
    path: '/absences/applications/',
    component: () => import('../../src/views/AbsenceApplications.vue'),
    meta: { permissionSpace: 'absence-applications' },
  },
  {
    name: 'absence-applications:detail',
    path: '/absences/applications/:id/moderate/',
    component: () => import('../../src/views/AbsenceApplicationsDetail.vue'),
    meta: {
      permissionSpace: 'absence-applications',
      breadcrumbs: [
        new Breadcrumb('absence-applications', async () => 'Offene Anfragen'),
      ]
    },
  },

  // Absence report
  {
    name: 'absence-reports',
    path: '/absences/reports/',
    component: () => import('../../src/views/v2/absence-report/Base.vue'),
    meta: { permissionSpace: 'absence-reports' },
  },

  // Absence
  {
    name: 'absences',
    path: '/absences/',
    component: () => import('../../src/views/Absences.vue'),
    meta: { permissionSpace: 'absence' },
  },
  {
    name: 'absences:year:month',
    path: '/absences/:year/:month/',
    component: () => import('../../src/views/Absences.vue'),
    meta: { permissionSpace: 'absence' },
  },

  // Activity
  {
    name: 'reports:who-is-online',
    path: '/reports/activity/',
    component: () => import('../../src/views/Activity.vue'),
    meta: { permissionSpace: 'monthly_report' },
  },
  {
    name: 'reports:who-is-online:detail',
    path: '/reports/activity/:employeeId/',
    redirect: to => {
      const date = new Date();
      const year = String(date.getFullYear());
      const month = String(date.getMonth() + 1);
      return '/reports/employees/' + year + '/' + month + '/' + to.params.employeeId + '/';
    },
    meta: { permissionSpace: 'monthly_report' },
  },

  // Reports Review (Fehlerprüfung)
  {
    name: 'reports:review',
    path: '/reports/review/',
    redirect: to => {
      const date = new Date();
      const year = String(date.getFullYear());
      const month = String(date.getMonth() + 1).padStart(2, '0');
      return '/reports/review/' + year + '/' + month + '/';
    },
    meta: { permissionSpace: 'monthly_report' },
  },
  {
    name: 'reports:review:year:month',
    path: '/reports/review/:year/:month/',
    component: () => import('../../src/views/reports-review/ReportsReview.vue'),
    meta: { permissionSpace: 'monthly_report' },
  },
  // Monthly Report
  {
    name: 'reports:employees',
    path: '/reports/employees/',
    component: () => import('../../src/views/MonthlyReports.vue'),
    meta: { permissionSpace: 'monthly_report' },
  },
  {
    name: 'reports:employees:year:month',
    path: '/reports/employees/:year/:month/',
    component: () => import('../../src/views/MonthlyReports.vue'),
    meta: { permissionSpace: 'monthly_report' },
  },
  {
    name: 'reports:employees:year:month:detail',
    path: '/reports/employees/:year/:month/:reportId/',
    component: () => import('../../src/views/MonthlyReportsDetail.vue'),
    meta: {
      permissionSpace: 'monthly_report',
    },
    children: [
      {
        name: 'reports:employees:year:month:detail:absence:add',
        path: 'absences/add/:newAbsenceDate/',
        component: () => import('../../src/views/MonthlyReportsDetail.vue'),
      },
      {
        name: 'reports:employees:year:month:detail:absence:edit',
        path: 'absences/:absenceId/',
        component: () => import('../../src/views/MonthlyReportsDetail.vue'),
      },

      {
        name: 'reports:employees:year:month:detail:wtt:add',
        path: 'working-time-transactions/add/',
        component: () => import('../../src/views/MonthlyReportsDetail.vue'),
      },
      {
        name: 'reports:employees:year:month:detail:wtt:edit',
        path: 'working-time-transactions/:workingTimeTransactionId/',
        component: () => import('../../src/views/MonthlyReportsDetail.vue'),
      },

      {
        name: 'reports:employees:year:month:detail:absence-transaction:add',
        path: 'absence-transactions/add/',
        component: () => import('../../src/views/MonthlyReportsDetail.vue'),
      },
      {
        name: 'reports:employees:year:month:detail:absence-transaction:edit',
        path: 'absence-transactions/:absenceTransactionId/',
        component: () => import('../../src/views/MonthlyReportsDetail.vue'),
      },
      {
        name: 'reports:employees:year:month:detail:absence-rule:edit',
        path: 'absence-transactions/:absenceRuleId/',
        component: () => import('../../src/views/MonthlyReportsDetail.vue'),
      },

      {
        name: 'reports:employees:year:month:detail:workday:add',
        path: 'workdays/add/:newWorkdayDate/',
        component: () => import('../../src/views/MonthlyReportsDetail.vue'),
      },
      {
        name: 'reports:employees:year:month:detail:workday:edit',
        path: 'workdays/:workdayId/',
        component: () => import('../../src/views/MonthlyReportsDetail.vue'),
      },
      {
        name: 'reports:employees:year:month:detail:workday:edit:timespan:edit',
        path: 'workdays/:workdayId/timespan/:timespanId/',
        component: () => import('../../src/views/MonthlyReportsDetail.vue'),
      },
      {
        name: 'reports:employees:year:month:detail:workday:edit:timespan:add',
        path: 'workdays/:workdayId/timespan/add/',
        component: () => import('../../src/views/MonthlyReportsDetail.vue'),
      },
    ],
  },
  // {
  //   name: 'reports:employees:year:month:detail:absence:edit',
  //   path: '/reports/employees/:year/:month/:reportId/absence/:absenceId/',
  //   component: () => import('../../src/views/MonthlyReportsDetail.vue'),
  //   meta: { permissionSpace: 'monthly_report' },
  // },

  // Settings
  {
    name: 'settings',
    path: '/settings/',
    component: () => import('../../src/views/Settings.vue'),
    meta: { permissionSpace: 'settings' },
  },

  // Support
  {
    name: 'support:contact',
    path: '/support/contact/',
    component: () => import('../../src/views/SupportForm.vue'),
    meta: { permissionSpace: 'support_tickets' },
  },
  {
    name: 'support',
    path: '/support/',
    component: () => import('../../src/views/Support.vue'),
    meta: { permissionSpace: 'support_tickets' },
  },

  // Account
  {
    name: 'account',
    path: '/account/profile/',
    component: () => import('../../src/views/UserAccount.vue'),
  },
  {
    path: '/account/institution/',
    component: () => import('../../src/views/Institution.vue'),
    // meta: { permissionSpace: 'acc' },
  },

  // Absences
  {
    name: 'my-absences:add',
    path: '/my-absences/applications/add/',
    component: () => import('../../src/views/EmployeeAbsenceDetail.vue'),
    meta: {
      permissionSpace: 'absence-applications',
      breadcrumbs: [
        new Breadcrumb('my-absences', async () => 'Abwesenheiten'),
      ],
    },
  },
  {
    name: 'my-absences:detail',
    path: '/my-absences/applications/:id/',
    component: () => import('../../src/views/EmployeeAbsenceDetail.vue'),
    meta: {
      permissionSpace: 'absence-applications',
      breadcrumbs: [
        new Breadcrumb('my-absences', async () => 'Abwesenheiten'),
      ],
    },
  },
  {
    name: 'my-absences',
    path: '/my-absences/',
    component: () => import('../../src/views/EmployeeAbsence.vue'),
    meta: { permissionSpace: 'absence' },
  },

  // Reports
  {
    name: 'mobile-reports:employee:working-time-transactions',
    path: '/mobile/reports/employees/:year/:month/:employeeId/working-time-transactions/',
    component: () => import('../../src/views/EmployeeReportWorkingTimeTransactions.vue'),
    meta: { permissionSpace: 'monthly_report' },
  },
  {
    name: 'mobile-reports:employee:params',
    path: '/mobile/reports/employees/:year/:month/:employeeId/',
    component: () => import('../../src/views/EmployeeReport.vue'),
    meta: { permissionSpace: 'monthly_report' },
  },
  {
    name: 'mobile-reports:employees',
    path: '/mobile/reports/employees/',
    component: () => import('../../src/views/EmployeeReport.vue'),
    meta: { permissionSpace: 'monthly_report' },
  },
  {
    name: 'mobile-reports:employees:statistics',
    path: '/mobile/reports/employees/:year/:month/statistics/',
    component: () => import('../../src/views/EmployeeReportStats.vue'),
    meta: { permissionSpace: 'monthly_report' },
  },

  // Debug
  {
    name: 'debug-desktop',
    path: '/debug/',
    component: () => import('../../src/views/Debug.vue')
  },
  {
    name: 'debug-mobile',
    path: '/debug-mobile/',
    component: () => import('../../src/views/DebugMobile.vue')
  },

  // Errors
  {
    name: 'errors',
    path: '/errors/:type/',
    component: () => import('../../src/views/Error.vue'),
    meta: {
      hideChrome: true,
      allowGuests: true,
    },
  },
  {
    name: 'globals:v2:errors',
    path: '/error/',
    component: () => import('../../src/views/v2/Error.vue'),
    meta: {
      hideChrome: true,
      allowGuests: true,
    },
  },

  // Payment
  {
    name: 'billing',
    path: '/billing/',
    component: () => import('../../src/views/billing/Billing.vue'),
    meta: { permissionSpace: 'billing' },
  },
  {
    name: 'billing_alt',
    path: '//billing/',
    redirect: () => {
      return { name: 'billing' };
    },
  },

  // Account
  {
    name: 'license',
    path: '/account/license/',
    component: () => import('../../src/views/License.vue'),
    meta: { permissionSpace: 'licenses' },
  },
  {
    name: 'data-processing-agreement',
    path: '/account/data-processing-agreement/',
    component: () => import('../../src/views/DataProcessingAgreement.vue'),
    meta: { permissionSpace: 'contracts' },
  },

  // Billing
  {
    name: 'payment-methods:list',
    path: '/account/billing/payment-methods/',
    component: () => import('../../src/views/PaymentMethods.vue'),
    meta: { permissionSpace: 'billing' },
  },

  // Startpage
  {
    name: 'start',
    path: '',
    component: () => import('../../src/views/Home.vue'),
    // redirect: { path: '/time-tracking/',},
    meta: {
      allowGuests: true,
      hideChrome: true,
      transparentStatusBar: true,
    }
  },

  // Imprint
  {
    name: 'imprint',
    path: '/imprint/',
    component: () => import('../../src/views/Imprint.vue'),
    meta: {
      allowGuests: true,
      hideChrome: true,
      transparentStatusBar: true,
    },
  },

  // Privacy Policy
  // Todo: Add Privacy Policy

  // Onboarding
  {
    name: 'onboarding',
    path: '/onboarding/',
    component: () => import('../../src/views/onboarding/Onboarding.vue'),
    meta: {
      hideChrome: true,
      page: {
        title: 'Einrichtung',
      },
    },
  },

  // Labs
  {
    name: 'labs:list',
    path: '/labs/',
    component: () => import('../../src/views/experiments/Experiments.vue'),
  },
  {
    name: 'labs:detail',
    path: '/labs/:slug/',
    component: () => import('../../src/views/experiments/ExperimentDetail.vue'),
    meta: {
      hideChrome: true,
    },
  },

  // Playground
  {
    name: 'playground-01',
    path: '/playground/01/',
    component: () => import('../../src/views/playground/Playground01.vue'),
  },

  {
    name: 'playground-02',
    path: '/playground/02/',
    component: () => import('../../src/views/playground/Playground02.vue'),
  },

  // Helper pages
  {
    name: 'generic-api-playground',
    path: '/generic-api/',
    component: () => import('../../src/views/GenericApi.vue'),
  },

  {
    name: 'dynamic-tags',
    path: '/dynamic-tags/',
    component: () => import('../../src/generics/list/Base.vue'),
    meta: {
      page: {
        title: 'Labels',
        hasSearch: true,
      }
    },
  },

]

const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_BASE_URL),
  routes
})

// Check if there is an auth code in the URLs params
router.beforeEach(async (to, _from, next) => {
  const code = to.query.code as undefined | string;
  if (code) {
    next({ name: 'authentication:code', query: { c: code, next: to.path } });
    return;
  }

  next();



  // const [error, success] = await codeBackend.login(code);
  // if (!success) {
  //   next({ name: 'globals:v2:errors', query: { e: error } });
  //   return;
  // }

  // next();
});

router.afterEach((to, from) => {
  if (to.query.back == from.fullPath) {
    to.meta = to.meta || {};
    to.meta.computedTransition = 'slide-left';
    return;
  }
  if (from.query.back == to.fullPath) {
    to.meta = to.meta || {};
    to.meta.computedTransition = 'slide-right';
    return;
  }

  if (to.query.backFromCard == from.fullPath) {
    to.meta = to.meta || {};
    to.meta.computedTransition = 'show-card';
    return;
  }
  if (from.query.backFromCard == to.fullPath) {
    to.meta = to.meta || {};
    to.meta.computedTransition = 'hide-card';
    return;
  }
})

// Check if this view is allowed for guests
router.beforeEach((to, _from, next) => {
  const isGuest = localStorage.accessToken === undefined;

  if (to.meta.allowGuests || (!isGuest)) {
    next();
    return
  }
  next({ name: 'authentication:login', query: { next: to.fullPath } });
});


// Check if this view is allowed for non-employees
router.beforeEach(async (to, _from, next) => {
  if (!to.meta.requiresEmployee) {
    next();
    return
  }

  const account = (await accountStore.retrieveAllLocalOrRemote())[0].entry;
  if (!account) {
    next({ name: 'globals:v2:errors', query: { e: 'reload-page' } });
    return
  }
  if (account.role != 'employee') {
    next({ name: 'employees:list' });
    return
  }

  next();
});

// Close mobile menu
router.afterEach(async (to, _from) => {
  showMenu.value = false;
});

// Set title
const setTitle = function (c: any) {
  const title = c?.title;
  if (!title) return;
  document.title = title + " – " + branding.name;
}

router.beforeEach((to, _from, next) => {
  if (to.path.startsWith('//')) {
    next({ path: to.path.replace('//', '/') });
    return;
  }
  next();
});

router.beforeEach((to, _from, next) => {
  const component = (to.matched[0]?.components?.default as any);
  if (!component) {
    next();
    return;
  };

  if (typeof component == 'function') {
    component().then((c: any) => setTitle(c?.default)).catch(() => {})
  } else {
    setTitle(component)
  }

  next();
});

// Check if the onboarding is complete
router.beforeEach((to, _from, next) => {
  // Don't redirect if already going to onboarding
  if (to.name === 'onboarding') {
    next();
    return;
  }

  const institution = session.institution;
  if (!institution) {
    next();
    return;
  }

  if (!institution.onboarded_at && institution.created_at > '2026-01-22') {
    next({ name: 'onboarding' });
    return;
  }

  next();
});

(window as any).router = router;

installStaleChunkReload(router);

export default router
